import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { Badge } from "@/components/StatusBadge";
import { ContactQuickLinks } from "@/components/ContactQuickLinks";
import { KeyFactsStrip, type KeyFact } from "@/components/record/KeyFactsStrip";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField } from "@/components/record/fields";
import type { RelatedRecordRow } from "@/components/record/RelatedRecords";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TenantsIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type { ActivityResource, LeaseHistoryItemResource, Page, TenantResource } from "@/lib/api-types";
import { useIsOwner, useIsReadOnly } from "@/lib/current-actor";
import { LEASE_STATUS_LABELS, TENANT_STATUS_FILL, TENANT_STATUS_LABELS } from "@/lib/enum-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

/**
 * `/tenants-leases/:id` (Story 6.1). Built on `RecordPage`/`FieldRow` —
 * never a parallel page implementation, mirroring `PropertyRecord.tsx`'s own
 * rule exactly. Every field the AC names renders (VC-6.1-03); the lease-
 * history section is a real, genuinely-wired `RelatedRecordsSection`
 * (VC-6.1-06, VC-6.1-15) — a real `GET /tenants/{id}/leases` request, empty
 * today because no `Lease` exists yet (Story 6.3's own future job), never a
 * hardcoded placeholder. No payment-history section renders anywhere on this
 * page (VC-6.1-07) — not even an empty state; Phase 1b adds it as its own
 * new section, not something this story stubs out early.
 *
 * Story 6.2 adds two things: the one-click contact links (real `tel:`/
 * `sms:`/`mailto:` anchors built from the tenant's own stored phone/email,
 * VC-6.2-09/VC-6.2-10) and the inline communication log (VC-6.2-11).
 * `RelatedRecordsSection` is deliberately **not** reused for the log — that
 * component is built for read-only related rows navigating to another
 * record (a click on any row navigates away, spec's own `RelatedRecords.tsx`
 * review question); a communication log is a different shape entirely: an
 * appendable list with its own inline "add a note" form and no per-row
 * destination to navigate to. The log is built directly in this file
 * instead, the same way the contact links are — both are this story's own,
 * modest, tenant-record-specific UI, not a second generic component whose
 * only real caller would be this one page.
 */

const STATUS_OPTIONS = Object.entries(TENANT_STATUS_LABELS).map(([value, label]) => ({ value, label }));

const CONTACT_METHOD_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "text", label: "Text" },
];

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function formatYesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function formatOccupants(value: TenantResource["additional_occupants"]): string {
  if (!value || value.length === 0) {
    return "—";
  }
  return value.map((occupant) => `${occupant.name} (${occupant.relationship})`).join(", ");
}

function formatPets(value: TenantResource["pets"]): string {
  if (!value || value.length === 0) {
    return "—";
  }
  return value.map((pet) => (pet.breed ? `${pet.type} (${pet.breed})` : pet.type)).join(", ");
}

function formatVehicles(value: string[] | null): string {
  return value && value.length > 0 ? value.join(", ") : "—";
}

const NO_LEASE_ON_FILE_MESSAGE = "No lease on file";

/**
 * Story 15.8, Item 1's own "current lease" resolution: the tenant's
 * in-force lease (`in_force`, computed server-side through AD-2's single
 * `is_in_force()` predicate — never re-derived here from `status`), or,
 * absent one, the most recent lease by `start_date`. `leases` is already
 * sorted most-recent-`start_date`-first by `GET /tenants/{id}/leases`
 * (`app.repo.leases.list_leases_for_tenant`'s own `order_by`), so the
 * fallback is simply the first row once no in-force one is found —
 * `undefined` for a tenant with no lease at all (VC-15.8-03's own honest
 * empty state).
 */
function currentLeaseFor(leases: LeaseHistoryItemResource[]): LeaseHistoryItemResource | undefined {
  return leases.find((lease) => lease.in_force) ?? leases[0];
}

/** The key-facts strip's own four stats, built from `currentLeaseFor`'s
 * result — an honest "—"/"No lease on file" state on every stat when
 * `currentLease` is `undefined` (VC-15.8-03), never a crash and never a
 * fabricated value. */
function tenantKeyFacts(currentLease: LeaseHistoryItemResource | undefined): KeyFact[] {
  if (currentLease === undefined) {
    return [
      { label: "Property / unit", value: NO_LEASE_ON_FILE_MESSAGE },
      { label: "Monthly rent", value: "—" },
      { label: "Lease start", value: "—" },
      { label: "Lease end", value: "—" },
    ];
  }
  return [
    {
      label: "Property / unit",
      value: `${currentLease.property_nickname} — ${currentLease.unit_label}`,
      ...(currentLease.prop_id ? { href: `/properties/${currentLease.prop_id}` } : {}),
    },
    { label: "Monthly rent", value: formatMoneyCell(currentLease.monthly_rent_cents), tone: "money" },
    { label: "Lease start", value: formatDateCell(currentLease.start_date) },
    { label: "Lease end", value: currentLease.end_date ? formatDateCell(currentLease.end_date) : "—" },
  ];
}

/** An emptied text field is sent as `null` — mirrors `PropertyRecord.tsx`'s
 * identical `patchBodyFor`: a cleared value returns the field to
 * "unanswered", not to an empty string a date/boolean column would refuse. */
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  if (fieldKey === "id_verified") {
    return { id_verified: payload.value === "true" };
  }
  return { [fieldKey]: payload.value === "" ? null : payload.value };
}

export function TenantRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  //: `PATCH /tenants/{id}` is `role=Role.OWNER` (Boundaries & Constraints) —
  //: every edit affordance is withheld from anyone but a confirmed Owner,
  //: mirroring `PropertyRecord.tsx`'s identical Tier A-established rule (a
  //: Member is not Read-only, so `useIsReadOnly()` alone would render these
  //: fully editable for a Member, who would then 403).
  const isOwner = useIsOwner();
  //: `POST`/`DELETE /activities` are `role=Role.MEMBER` (spec Boundaries) —
  //: the "add a note" affordance is withheld only from a confirmed
  //: Read-only account, not from a Member the way the edit fields above are
  //: (`useIsOwner` alone would wrongly hide it from a Member, who can
  //: legitimately add a note).
  const isReadOnly = useIsReadOnly();

  const queryKey = useMemo(() => ["tenants", "record", id] as const, [id]);
  const activitiesQueryKey = useMemo(() => ["tenants", "record", id, "activities"] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<TenantResource>(`/tenants/${id}`),
    enabled: id !== undefined,
  });

  //: VC-6.1-06/VC-6.1-15: a real network round-trip, always issued — never
  //: gated behind "only fetch once leases might exist" (there is no such
  //: signal today; the whole point is this fires unconditionally and stays
  //: correct the day `Lease` ships).
  const leasesQuery = useQuery({
    queryKey: ["tenants", "record", id, "leases"] as const,
    queryFn: () => apiFetch<Page<LeaseHistoryItemResource>>(`/tenants/${id}/leases`),
    enabled: id !== undefined,
  });

  //: VC-6.2-11: a real, always-issued `GET /activities?entity_type=tenant&
  //: entity_id=...` request — the communication log's own data, filtered to
  //: this tenant and only this tenant (VC-6.2-07's frontend half).
  const activitiesQuery = useQuery({
    queryKey: activitiesQueryKey,
    queryFn: () =>
      apiFetch<Page<ActivityResource>>(
        `/activities?entity_type=tenant&entity_id=${encodeURIComponent(id ?? "")}`,
      ),
    enabled: id !== undefined,
  });

  useEffect(() => {
    document.title = recordQuery.data ? `${recordQuery.data.full_name} — PropDesk` : "Tenant — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: TenantResource) {
    queryClient.setQueryData<TenantResource>(queryKey, updated);
  }

  async function sendPatch(
    row: TenantResource,
    field: DataTableColumn<TenantResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<TenantResource>(`/tenants/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(field.key, payload)),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<TenantResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  const [removeOpen, setRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | undefined>(undefined);
  const [noteBody, setNoteBody] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);

  /** VC-6.2-04: adding a note is always a new `POST /activities` row —
   * there is no update path anywhere in this form, so a correction is
   * always a second note, never a rewrite of the first. Refetches the log
   * afterward (rather than a hand-rolled cache splice) so the newest-first
   * order the backend itself owns (`app/repo/activities.py`) is always what
   * renders, never a client-side guess at where the new row belongs. */
  async function handleAddNote() {
    if (id === undefined) {
      return;
    }
    const trimmed = noteBody.trim();
    if (trimmed === "") {
      return;
    }
    setIsSubmittingNote(true);
    setNoteError(undefined);
    try {
      await apiFetch<ActivityResource>("/activities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entity_type: "tenant", entity_id: id, body: trimmed }),
      });
      setNoteBody("");
      await queryClient.invalidateQueries({ queryKey: activitiesQueryKey });
    } catch (error) {
      setNoteError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmittingNote(false);
    }
  }

  async function confirmRemove() {
    if (id === undefined || recordQuery.data === undefined) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/tenants/${id}`, { method: "DELETE" });
      writeRecordToCache({ ...recordQuery.data, is_archived: true });
      setRemoveOpen(false);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  /** Recovery, not a destructive action — no confirmation step, mirroring
   * `PropertyRecord.tsx`'s/`UserRecord.tsx`'s identical un-archive/restore
   * shape (spec Intent: "delete/restore mirrors restore_user's and Story
   * 5.6's own restore_property's identical shape"). */
  async function handleRestore() {
    if (id === undefined) {
      return;
    }
    setIsRestoring(true);
    setRestoreError(undefined);
    try {
      const restored = await apiFetch<TenantResource>(`/tenants/${id}/restore`, { method: "POST" });
      writeRecordToCache(restored);
      showToast(`${restored.full_name} is active again.`);
    } catch (error) {
      setRestoreError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRestoring(false);
    }
  }

  const isArchived = recordQuery.data?.is_archived ?? false;

  const fields = useMemo<RecordField<TenantResource>[]>(() => {
    const textEdit = isOwner && !isArchived ? { edit: { kind: "text" as const } } : {};
    const statusEdit =
      isOwner && !isArchived ? { edit: { kind: "select" as const, options: STATUS_OPTIONS } } : {};
    const contactMethodEdit =
      isOwner && !isArchived ? { edit: { kind: "select" as const, options: CONTACT_METHOD_OPTIONS } } : {};
    const yesNoEdit = isOwner && !isArchived ? { edit: { kind: "select" as const, options: YES_NO_OPTIONS } } : {};

    const definitions: RecordField<TenantResource>[] = [
      // -- The fields that matter — render immediately. --------------------- //
      { key: "full_name", ...textEdit },
      { key: "mobile_phone", ...textEdit },
      { key: "email", ...textEdit },
      { key: "status", render: (row) => TENANT_STATUS_LABELS[row.status] ?? row.status, ...statusEdit },
      // -- Everything else — behind "More details". ------------------------ //
      // `move_in_date`/`move_out_date` both removed entirely by Story 15.10
      // (VC-15.10-01): the key-facts strip's own "Lease start"/"Lease end"
      // stats (Story 15.8) already show this information prominently, so
      // neither field renders on this page at all anymore — the column/API
      // fields themselves are untouched (VC-15.10-02), still real, still
      // editable via PATCH, just no longer rendered here.
      { key: "preferred_contact_method", primary: false, ...contactMethodEdit },
      {
        key: "date_of_birth",
        primary: false,
        render: (row) => (row.date_of_birth ? formatDateCell(row.date_of_birth) : "—"),
        ...textEdit,
      },
      { key: "employer", primary: false, ...textEdit },
      { key: "emergency_contact_name", primary: false, ...textEdit },
      { key: "emergency_contact_phone", primary: false, ...textEdit },
      { key: "additional_occupants", primary: false, render: (row) => formatOccupants(row.additional_occupants) },
      { key: "pets", primary: false, render: (row) => formatPets(row.pets) },
      { key: "vehicles", primary: false, render: (row) => formatVehicles(row.vehicles) },
      { key: "id_verified", primary: false, render: (row) => formatYesNo(row.id_verified), ...yesNoEdit },
      {
        key: "id_verified_date",
        primary: false,
        render: (row) => (row.id_verified_date ? formatDateCell(row.id_verified_date) : "—"),
        ...textEdit,
      },
      { key: "id_verified_by", primary: false, render: (row) => row.id_verified_by ?? "—" },
      { key: "notes", primary: false, ...textEdit },
    ];
    return definitions;
  }, [isOwner, isArchived]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;

  //: VC-6.1-06/VC-6.5-08/VC-6.5-09: a real `RelatedRecordsSection`, backed
  //: by `GET /tenants/{id}/leases` — real, non-empty the moment `Lease`
  //: and `LeaseTenant` exist (Story 6.5), and includes a lease where this
  //: tenant is a co-tenant, not only ones where they were the lease's sole
  //: tenant (the backend's own `list_leases_for_tenant` join, never a
  //: "primary tenant" field). `leasesQuery.data?.data` is the real response
  //: body; falling back to `[]` only covers the loading window before that
  //: response arrives, not a substitute for the real fetch.
  const leaseRows: RelatedRecordRow[] = (leasesQuery.data?.data ?? []).map(
    (row): RelatedRecordRow => ({ ...row }),
  );

  //: Story 15.8's own "current lease" resolution (Item 1) — the same
  //: `leasesQuery` this page already fetches for its own "Lease history"
  //: section below, no second round trip.
  const currentLease = currentLeaseFor(leasesQuery.data?.data ?? []);

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      {restoreError ? (
        <p role="alert" className="mb-4 text-meta text-danger">
          {restoreError}
        </p>
      ) : null}
      {/*
        Story 6.2's one-click contact links (VC-6.2-09/VC-6.2-10) — real
        `tel:`/`sms:`/`mailto:` anchors, never a click handler: this is what
        lets "open the device's normal phone or mail app" work at all,
        keyboard/right-click/"open in a new tab" included, the same reason
        `RelatedRecords.tsx`'s own identifier cell is a real `<Link>` rather
        than a bare `onClick`. `buttonClassName("secondary", ...)` gives each
        one the real `h-tap-target-min` (44px) button height; `min-w-tap-
        target-min` guarantees the matching 44px minimum width even for the
        shortest label ("Call"). `mobile_phone` is a required field on every
        tenant (Story 6.1) so `tel:`/`sms:` always render; `email` is
        nullable, so the `mailto:` link renders only when one is actually on
        file (VC-6.2-10) — never a broken link to nothing.
      */}
      {recordQuery.data ? (
        <ContactQuickLinks
          mobilePhone={recordQuery.data.mobile_phone}
          email={recordQuery.data.email}
          className="mb-4"
        />
      ) : null}
      <RecordPage<TenantResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => row.full_name}
        backLink={{ label: "Tenants", href: "/tenants-leases" }}
        icon={TenantsIcon}
        tint="violet"
        //: Story 15.8's own status pill (Intent: "a status pill next to
        //: the record's own name") — the tenant's real `status` field,
        //: through the one `TENANT_STATUS_FILL` map (`lib/enum-labels.ts`),
        //: never a second, page-local fill decision.
        statusBadge={(row) => (
          <Badge
            label={TENANT_STATUS_LABELS[row.status] ?? row.status}
            fill={TENANT_STATUS_FILL[row.status] ?? "neutral"}
          />
        )}
        keyFacts={() => <KeyFactsStrip facts={tenantKeyFacts(currentLease)} />}
        {...(isArchived
          ? {
              primaryAction: {
                label: isRestoring ? "Restoring…" : "Restore",
                onClick: () => void handleRestore(),
                disabled: isRestoring,
              },
            }
          : {})}
        {...(!isArchived && isOwner && recordQuery.data
          ? {
              secondaryActions: [
                {
                  label: "Remove",
                  ariaLabel: `Remove ${recordQuery.data.full_name}`,
                  onClick: () => setRemoveOpen(true),
                },
              ],
            }
          : {})}
        onCommitEdit={commitEdit}
        relatedRecords={[
          {
            title: "Lease history",
            itemNamePlural: "leases",
            rows: leaseRows,
            columns: [
              {
                key: "start_date",
                label: "Lease",
                render: (row) => (typeof row.start_date === "string" ? formatDateCell(row.start_date) : "—"),
              },
              {
                //: Story 15.6 (VC-15.6-01/02) — `end_date` was already
                //: fetched via `GET /tenants/{id}/leases` (`LeaseHistoryItemResource`
                //: has carried it since Epic 6) but never rendered. `null`
                //: is a real, legal state (month-to-month, or a lease still
                //: being set up), so it renders the same honest "—" this
                //: page's own `date_of_birth`/`id_verified_date` fields
                //: already use for an absent optional date — never a
                //: blank cell indistinguishable from a loading state.
                key: "end_date",
                label: "End date",
                render: (row) => (typeof row.end_date === "string" ? formatDateCell(row.end_date) : "—"),
              },
              {
                key: "status",
                label: "Status",
                render: (row) =>
                  typeof row.status === "string" ? (LEASE_STATUS_LABELS[row.status] ?? row.status) : "—",
              },
            ],
            //: Story 6.5's real lease record page — `LeaseRecord.tsx`,
            //: wired at `/tenants-leases/leases/:id` (a sibling path under
            //: this same nav destination, not the bare `/tenants-leases/
            //: :id` a tenant's own record already occupies — see `main.
            //: tsx`'s own comment on this routing choice). Replaces the old
            //: self-referencing `#lease-${row.id}` placeholder anchor,
            //: which pointed back at this exact page with no real
            //: destination behind it.
            getHref: (row) => `/tenants-leases/leases/${row.id}`,
            emptyState: {
              body: "Once a lease exists for this tenant, it will appear here — across every renewal.",
            },
          },
        ]}
      />
      {/*
        Story 6.2's communication log (VC-6.2-11) — inline on this page,
        never a separate route (spec judgement call 5): free text, stamped
        with the date and the author, appended rather than replacing, never
        silently edited (this AC's own words) — there is no edit affordance
        anywhere in this section, only "add a note", because there is no
        `PATCH /activities/{id}` route to call (judgement call 2). Rendered
        unconditionally, not gated behind `recordQuery.data` — a
        Read-only viewer, or the loading window before the tenant record
        itself resolves, still gets a real, empty-or-populated log, the same
        "never hidden, never a fake row" discipline `RelatedRecordsSection`
        already established for lease history.
      */}
      <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
        <h2 className="text-heading font-semibold text-ink">Communication log</h2>
        {/*
          `!isReadOnly` — not `isOwner` — mirrors `POST /activities`'s own
          `role=Role.MEMBER` exactly: both a Member and an Owner see this
          form, only a Read-only account does not (VC-6.2-11's own second
          half).
        */}
        {!isReadOnly ? (
          <form
            className="mt-4 flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void handleAddNote();
            }}
          >
            <label className="flex flex-col gap-1">
              <span className="sr-only">Add a note</span>
              <textarea
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
                placeholder="What happened? For example: Called about the renewal — she wants to stay."
                rows={3}
                className="w-full rounded-md border border-border-input bg-surface px-3 py-2 text-body text-ink placeholder-ink-tertiary focus:border-border-focus"
              />
            </label>
            {noteError ? (
              <p role="alert" className="text-meta text-danger">
                {noteError}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button type="submit" disabled={noteBody.trim() === "" || isSubmittingNote}>
                {isSubmittingNote ? "Saving…" : "Add note"}
              </Button>
            </div>
          </form>
        ) : null}
        <div className="mt-4">
          {(activitiesQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-body text-ink-secondary">No notes yet.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {(activitiesQuery.data?.data ?? []).map((note) => (
                <li key={note.id} className="rounded-lg border border-border-hairline bg-surface p-4">
                  <p className="whitespace-pre-wrap text-body text-ink">{note.body}</p>
                  <p className="mt-2 text-meta text-ink-secondary">
                    {formatDateCell(note.created_at)}
                    {note.author_display_name ? ` — ${note.author_display_name}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      {recordQuery.data ? (
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={(open) => {
            setRemoveOpen(open);
            if (!open) {
              setRemoveError(undefined);
            }
          }}
          title="Remove this tenant?"
          description={`Removing ${recordQuery.data.full_name} takes their record out of the active list right away. It can be restored for 30 days.`}
          confirmLabel={`Remove ${recordQuery.data.full_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={contentRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
